import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Minus, Plus, Grid3X3, Rows3 } from 'lucide-react';
import { buildVariantGrid } from '../../utils/variantStock';

const Swatch = ({ hex, size = 18 }) => (
  <span
    className="inline-block rounded-full border border-slate-300 shrink-0"
    style={{ width: size, height: size, background: hex || 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 3px,#fff 3px,#fff 6px)' }}
  />
);

const money = (n) => `$ ${Number(n || 0).toLocaleString('es-AR')}`;

/**
 * Selector de talle y color del POS.
 * - Colores como muestras; talles en el orden de la curva con el stock de la sucursal.
 * - Permite cargar varias unidades de varios talles en una sola apertura.
 * - Sin stock: deshabilitado, salvo que la organización permita stock negativo.
 * Teclado: 1-9 suma el talle N del color elegido, Enter agrega, Esc cierra.
 */
export default function SelectorTalleColor({
  product,
  siteId,
  allowNegative = false,
  enCarrito = {},          // { [variantId]: cantidad ya cargada en el ticket }
  getUnitPrice,            // (variant) => precio unitario con lista aplicada
  onConfirm,               // ([{ variant, cantidad }]) => void
  onClose,
  abbreviateName = false,
}) {
  const grid = useMemo(
    () => buildVariantGrid(product?.product_variants || [], { stockVariantes: product?.stock_variantes || [], siteId }),
    [product, siteId],
  );

  const stockColor = useCallback(
    (colorId) => grid.talles.reduce((acc, t) => acc + Math.max(0, grid.getCell(colorId, t)?.stock || 0), 0),
    [grid],
  );

  const [colorId, setColorId] = useState(() => {
    const conStock = grid.colores.find((c) => stockColor(c.id) > 0);
    return (conStock || grid.colores[0])?.id ?? null;
  });
  const [cantidades, setCantidades] = useState({});
  const [matriz, setMatriz] = useState(() => grid.colores.length > 1 && grid.colores.length <= 4 && grid.talles.length <= 8);

  const disponible = useCallback((variant, stock) => {
    const ya = Number(enCarrito[variant.id] || 0);
    const sel = Number(cantidades[variant.id] || 0);
    return stock - ya - sel;
  }, [enCarrito, cantidades]);

  const sumar = useCallback((variant, stock, delta = 1) => {
    setCantidades((prev) => {
      const actual = Number(prev[variant.id] || 0);
      const next = Math.max(0, actual + delta);
      if (delta > 0 && !allowNegative && stock - Number(enCarrito[variant.id] || 0) - next < 0) return prev;
      const copy = { ...prev };
      if (next === 0) delete copy[variant.id]; else copy[variant.id] = next;
      return copy;
    });
  }, [allowNegative, enCarrito]);

  const seleccion = useMemo(() => {
    const out = [];
    for (const c of grid.colores) {
      for (const t of grid.talles) {
        const cell = grid.getCell(c.id, t);
        const cant = cell ? Number(cantidades[cell.variant.id] || 0) : 0;
        if (cant > 0) out.push({ variant: cell.variant, cantidad: cant });
      }
    }
    return out;
  }, [grid, cantidades]);

  const totalUnidades = seleccion.reduce((a, s) => a + s.cantidad, 0);
  const totalImporte = seleccion.reduce((a, s) => a + s.cantidad * Number(getUnitPrice?.(s.variant) || 0), 0);

  const confirmar = useCallback(() => {
    if (!seleccion.length) return;
    onConfirm?.(seleccion);
  }, [seleccion, onConfirm]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
      if (e.key === 'Enter') { e.preventDefault(); confirmar(); return; }
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= 9) {
        const talle = grid.talles[n - 1];
        const cell = talle !== undefined ? grid.getCell(colorId, talle) : null;
        if (cell) { e.preventDefault(); sumar(cell.variant, cell.stock, 1); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [grid, colorId, sumar, confirmar, onClose]);

  const TalleButton = ({ cell, talle, compact = false }) => {
    if (!cell) return <div className={`${compact ? 'h-12' : 'h-16'} rounded-xl bg-slate-50/50 border border-dashed border-slate-100`} />;
    const { variant, stock } = cell;
    const cant = Number(cantidades[variant.id] || 0);
    const libre = disponible(variant, stock);
    const bloqueado = !allowNegative && libre <= 0 && cant === 0;
    const precio = getUnitPrice?.(variant);
    const precioPropio = variant.precio_override !== null && variant.precio_override !== undefined;
    return (
      <div className={`relative rounded-xl border transition-all ${cant > 0 ? 'border-[#51A7FD] bg-blue-50' : bloqueado ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 bg-white hover:border-[#51A7FD]'}`}>
        <button
          type="button"
          disabled={bloqueado}
          onClick={() => sumar(variant, stock, 1)}
          className={`w-full ${compact ? 'py-1.5' : 'py-2.5'} px-2 flex flex-col items-center disabled:cursor-not-allowed`}
          title={variant.sku ? `SKU ${variant.sku}` : undefined}
        >
          {!compact && <span className="text-[13px] font-black text-slate-800 uppercase">{talle || '—'}</span>}
          <span className={`text-[10px] font-bold ${stock <= 0 ? 'text-red-500' : 'text-slate-400'}`}>
            {stock <= 0 ? 'sin stock' : `${stock} disp.`}
          </span>
          {precioPropio && <span className="text-[9px] font-black text-[#51A7FD]">{money(precio)}</span>}
        </button>
        {cant > 0 && (
          <div className="absolute -top-2 -right-2 flex items-center gap-0.5">
            <button type="button" onClick={() => sumar(variant, stock, -1)} className="w-5 h-5 rounded-full bg-white border border-slate-300 flex items-center justify-center text-slate-500"><Minus size={10} /></button>
            <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-[#51A7FD] text-white text-[10px] font-black flex items-center justify-center">{cant}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-[2rem] w-full max-w-2xl shadow-2xl flex flex-col overflow-hidden max-h-[92vh]">
        <div className="bg-[#0B2A36] px-6 py-4 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <p className="text-[9px] font-black text-[#3FF2AA] uppercase tracking-widest mb-0.5">Elegí color y talle</p>
            <h3 className={`text-sm font-black text-white uppercase tracking-tight leading-tight ${abbreviateName ? 'line-clamp-1' : 'break-words'}`}>{product?.name}</h3>
          </div>
          <div className="flex items-center gap-2">
            {grid.colores.length > 1 && (
              <button type="button" onClick={() => setMatriz((m) => !m)} title={matriz ? 'Ver por color' : 'Ver todo'}
                className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20">{matriz ? <Rows3 size={16} /> : <Grid3X3 size={16} />}</button>
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto custom-scrollbar space-y-4">
          {!siteId && (
            <p className="text-[11px] font-bold text-amber-600 bg-amber-50 rounded-xl px-3 py-2">Elegí una sucursal para ver el stock por talle.</p>
          )}

          {matriz ? (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-1.5">
                <thead>
                  <tr>
                    <th />
                    {grid.talles.map((t) => <th key={t} className="text-[11px] font-black text-slate-600 uppercase">{t || '—'}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {grid.colores.map((c) => (
                    <tr key={c.id ?? 'sc'}>
                      <td className="pr-2 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2 text-[11px] font-black text-slate-700 uppercase">
                          {c.id && <Swatch hex={c.hex} size={14} />}{c.nombre || 'Único'}
                        </span>
                      </td>
                      {grid.talles.map((t) => (
                        <td key={t} className="min-w-[4.5rem]"><TalleButton cell={grid.getCell(c.id, t)} talle={t} compact /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              {grid.colores.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {grid.colores.map((c) => {
                    const on = c.id === colorId;
                    const st = stockColor(c.id);
                    return (
                      <button key={c.id ?? 'sc'} type="button" onClick={() => setColorId(c.id)}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-[11px] font-black uppercase transition-all ${on ? 'border-[#51A7FD] bg-blue-50 text-[#0B2A36]' : 'border-slate-200 text-slate-500 hover:border-slate-300'} ${st <= 0 ? 'opacity-60' : ''}`}>
                        <Swatch hex={c.hex} /> {c.nombre || 'Único'} <span className="text-[10px] font-bold text-slate-400 normal-case">{st}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
                {grid.talles.map((t, i) => (
                  <div key={t} className="relative">
                    <TalleButton cell={grid.getCell(colorId, t)} talle={t} />
                    {i < 9 && <span className="absolute bottom-1 left-1.5 text-[8px] font-bold text-slate-300">{i + 1}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-4 flex items-center justify-between gap-3 shrink-0">
          <div className="text-[11px] text-slate-500">
            {totalUnidades > 0
              ? <><b className="text-slate-800">{totalUnidades}</b> {totalUnidades === 1 ? 'unidad' : 'unidades'} · <b className="text-slate-800">{money(totalImporte)}</b></>
              : 'Tocá un talle para sumarlo (podés sumar varios).'}
          </div>
          <button type="button" disabled={!totalUnidades} onClick={confirmar}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#51A7FD] text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40">
            <Plus size={14} /> Agregar{totalUnidades ? ` ${totalUnidades}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
